// src/pages/Home/FormContact.jsx (ou ton chemin actuel)
import React, { useMemo, useState } from "react";
import { saveMessageContactForm } from "./../../services/contactformServices";
import { sendContactFormMail } from "./../../services/mailServices";
import SectionTitle from "../../components/ui/SectionTitle";
import { FiMail, FiUser, FiMessageSquare, FiSend, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

export default function FormContact() {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const canSubmit = useMemo(() => {
    const { name, email, message } = formData;
    return (
      status !== "sending" &&
      name.trim().length > 1 &&
      email.trim().length > 4 &&
      message.trim().length > 4
    );
  }, [formData, status]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMsg("");
    setStatus("sending");

    const { name, email, message } = formData;

    try {
      await saveMessageContactForm({ name, email, message });
      await sendContactFormMail({ name, email, message });

      setStatus("success");
      setFormData({ name: "", email: "", message: "" });

      // (option) auto-hide success
      setTimeout(() => setStatus("idle"), 3500);
    } catch (error) {
      console.error("Erreur formulaire contact:", error);
      setStatus("error");
      setErrorMsg(
        error?.response?.data?.message ||
          "Une erreur est survenue. Réessaie dans quelques instants."
      );
    }
  };

  return (
    <section
      id="contact"
      className="py-12 sm:py-16 bg-lightBackground dark:bg-darkBackground text-black dark:text-white"
    >
      <style>{`
        @keyframes fc-in { from { opacity:.0; transform: translateY(10px);} to {opacity:1; transform: translateY(0);} }
        @media (prefers-reduced-motion: reduce) {
          .fc-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="mx-auto max-w-4xl px-4">
        <SectionTitle
          title="Contactez-nous"
          subtitle="Une question ? Un essai ? Laissez-nous un message, on vous répond rapidement."
        />

        <div
          className={cn(
            "fc-anim mt-10 overflow-hidden rounded-3xl",
            "border border-slate-200/70 dark:border-slate-800",
            "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
            "shadow-sm"
          )}
          style={{ animation: "fc-in .35s ease-out both" }}
        >
          {/* bandeau léger */}
          <div
            aria-hidden
            className="h-1.5 w-full opacity-70"
            style={{
              background:
                "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
            }}
          />

          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  Votre email
                </label>

                <div className="relative">
                  <FiMail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="mail@gmail.com"
                    className={cn(
                      "h-11 w-full rounded-xl pl-10 pr-3 text-sm",
                      "bg-white/80 dark:bg-slate-950/40",
                      "border border-slate-200/70 dark:border-slate-800",
                      "text-slate-900 dark:text-slate-50",
                      "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                      "focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40",
                      "transition"
                    )}
                  />
                </div>
              </div>

              {/* Nom */}
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  Nom / Prénom
                </label>

                <div className="relative">
                  <FiUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Votre Nom Prénom"
                    className={cn(
                      "h-11 w-full rounded-xl pl-10 pr-3 text-sm",
                      "bg-white/80 dark:bg-slate-950/40",
                      "border border-slate-200/70 dark:border-slate-800",
                      "text-slate-900 dark:text-slate-50",
                      "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                      "focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40",
                      "transition"
                    )}
                  />
                </div>
              </div>

              {/* Message */}
              <div className="md:col-span-2">
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  Votre message
                </label>

                <div className="relative">
                  <FiMessageSquare className="pointer-events-none absolute left-3 top-4 text-slate-400" />
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Laissez votre message ici…"
                    className={cn(
                      "w-full rounded-xl pl-10 pr-3 py-3 text-sm",
                      "bg-white/80 dark:bg-slate-950/40",
                      "border border-slate-200/70 dark:border-slate-800",
                      "text-slate-900 dark:text-slate-50",
                      "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                      "focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40",
                      "transition",
                      "resize-none"
                    )}
                  />
                </div>

                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Réponse généralement sous 24–48h.
                </p>
              </div>
            </div>

            {/* Actions + feedback */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm">
                {status === "success" && (
                  <div className="inline-flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                    <FiCheckCircle />
                    Message envoyé ✅
                  </div>
                )}
                {status === "error" && (
                  <div className="inline-flex items-center gap-2 text-red-600 dark:text-red-400">
                    <FiAlertTriangle />
                    {errorMsg || "Erreur d’envoi"}
                  </div>
                )}
              </div>

              {/* Bouton submit natif (reco sans modifier ton Button) */}
              <button
                type="submit"
                disabled={!canSubmit}
                className={cn(
                  "inline-flex items-center justify-center gap-2",
                  "h-11 px-6 rounded-2xl font-semibold",
                  "bg-primary text-white border border-primary",
                  "shadow-sm transition",
                  "hover:bg-white hover:text-primary",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "focus:outline-none focus:ring-2 focus:ring-primary/40"
                )}
                aria-label="Envoyer le message"
                title="Envoyer"
              >
                {status === "sending" ? (
                  <>
                    <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    Envoi…
                  </>
                ) : (
                  <>
                    <FiSend className="text-lg" />
                    Envoyer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
